import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { ApiError, api } from "../../lib/api";
import { crmApi } from "../../lib/crm-api";
import { meetingTitle } from "../../lib/meetings-view";
import { queryKeys } from "../../lib/queryKeys";
import { salesApi } from "../../lib/sales-api";
import type {
  SalesMeetingDetail,
  SalesMeetingNote,
} from "../../lib/sales-types";
import { absoluteTime, relativeTime } from "../../lib/time";
import type { User } from "../../types";
import { Chip } from "../sales/NewCallDialog";
import { Avatar } from "../ui/Avatar";
import { Button } from "../ui/Button";
import { cn } from "../ui/cn";
import { FollowUpComposer } from "./FollowUpComposer";
import { GuestPicker } from "./GuestPicker";
import { dayMonth, longDay, timeRange } from "./MeetingRow";
import { NotesEditor, type NotesEditorHandle } from "./NotesEditor";

/** Where the pane opens: on the notes, the follow-up, or one of the edits. */
export type PanelFocus = "notes" | "follow-up" | "reschedule" | "who" | "guests";

/**
 * One meeting, beside the calendar or the feed it was opened from.
 *
 * Laid out like a Google Calendar event, because that is what reps read on
 * their phones all day: what and when at the top, where -- an address to tap
 * for the map, or a Meet link to join -- straight under it, then who. After
 * that the page is the **notes**, which are what somebody opens a past meeting
 * to write, and nothing sits between the header and them that has to be read
 * first.
 *
 * What is left is behind one press each. Following up is a button that opens
 * an email; changing the meeting -- moving it, who is going, calling it off --
 * is in the menu, because those are decisions and the page is mostly a record.
 * The meeting's own log and the notes on how to get there fold away under
 * History.
 *
 * On a desktop this is a pane on the right and the calendar or feed stays
 * where it was; on a phone it is the whole screen with a back arrow.
 */
export function MeetingPanel({
  id,
  user,
  focus,
  onClose,
  onOpenMeeting,
}: {
  id: number;
  user: User;
  /**
   * Open straight on the notes or the follow-up -- the feed's N and R -- or
   * on one of the edits, which is where the calendar card's pencil lands.
   */
  focus?: PanelFocus;
  onClose: () => void;
  onOpenMeeting: (id: number) => void;
}) {
  const client = useQueryClient();
  const notes = useRef<NotesEditorHandle>(null);
  const [composing, setComposing] = useState(false);
  const [menu, setMenu] = useState(false);
  const [editing, setEditing] = useState<"who" | "guests" | "reschedule" | null>(null);

  const meeting = useQuery({
    queryKey: queryKeys.salesMeeting(id),
    queryFn: () => salesApi.meeting(id),
    /* Not refetched behind the notes editor while somebody types; the editor
       holds the document and saves it itself. */
    refetchOnWindowFocus: false,
  });

  const act = useMutation({
    mutationFn: (action: () => Promise<SalesMeetingDetail | void>) => action(),
    onSuccess: (detail) => {
      if (detail) client.setQueryData(queryKeys.salesMeeting(id), detail);
      void client.invalidateQueries({ queryKey: queryKeys.salesRoot });
    },
  });

  /* The feed's N and R, and the card's pencil, once the meeting has loaded. */
  useEffect(() => {
    if (!meeting.data) return;
    if (focus === "notes") notes.current?.focus();
    if (focus === "follow-up") setComposing(true);
    if (focus === "reschedule" || focus === "who" || focus === "guests") setEditing(focus);
  }, [focus, meeting.data?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (meeting.isLoading || !meeting.data) {
    return (
      <PanelFrame onClose={onClose} title="">
        <p className="p-8 text-center text-sm text-faint">
          {meeting.isLoading ? "Loading…" : "That meeting is not here. It may have been deleted."}
        </p>
      </PanelFrame>
    );
  }

  const row = meeting.data;
  const isAdmin = user.role === "admin";
  const going = row.attendees.some((person) => person.userId === user.id);
  const canChange = going || isAdmin || row.attendees.length === 0;
  const open = row.status === "scheduled";
  const minutes = row.endAt
    ? Math.round((new Date(row.endAt).getTime() - new Date(row.startAt).getTime()) / 60_000)
    : null;
  const past = new Date(row.endAt ?? row.startAt) < new Date();

  return (
    <PanelFrame
      onClose={onClose}
      title={meetingTitle(row)}
      actions={
        canChange ? (
          <div className="relative">
            <button
              type="button"
              aria-label="More actions"
              aria-expanded={menu}
              onClick={() => setMenu((was) => !was)}
              className="flex size-8 items-center justify-center rounded-full text-muted hover:bg-surface-2 hover:text-content"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                <circle cx="12" cy="5" r="1.8" />
                <circle cx="12" cy="12" r="1.8" />
                <circle cx="12" cy="19" r="1.8" />
              </svg>
            </button>
            {menu ? (
              <Menu onClose={() => setMenu(false)}>
                {open ? (
                  <>
                    <MenuItem onClick={() => setEditing("reschedule")}>Edit title, time or place</MenuItem>
                    <MenuItem onClick={() => setEditing("who")}>Change who is going</MenuItem>
                    <MenuItem onClick={() => setEditing("guests")}>Change guests</MenuItem>
                    <MenuItem
                      danger
                      onClick={() => act.mutate(() => salesApi.cancelMeeting(id))}
                    >
                      Cancel the meeting
                    </MenuItem>
                  </>
                ) : (
                  <MenuItem onClick={() => act.mutate(() => salesApi.restoreMeeting(id))}>
                    Put it back in the diary
                  </MenuItem>
                )}
                {/* Only where it can succeed: the API refuses once anything
                    is written against a meeting. */}
                {isAdmin && row.noteCount === 0 && row.followUpCount === 0 ? (
                  <MenuItem
                    danger
                    onClick={() =>
                      act.mutate(async () => {
                        await salesApi.removeMeeting(id);
                        onClose();
                      })
                    }
                  >
                    Delete (booked in error)
                  </MenuItem>
                ) : null}
              </Menu>
            ) : null}
          </div>
        ) : null
      }
    >
      <div className="space-y-5 p-4">
        {/* ── When, where, with whom ─────────────────────────────────────── */}
        <section className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            {row.companyId ? (
              <Link
                to={`/crm/companies/${row.companyId}`}
                className="text-sm font-medium text-brand-600 hover:underline dark:text-brand-400"
              >
                {row.crmCompanyName ?? row.companyName}
              </Link>
            ) : (
              <span className="text-sm font-medium text-content">{row.companyName}</span>
            )}
            {!open ? (
              <span className="rounded-full bg-surface-2 px-2 py-0.5 text-[11px] font-medium text-muted-on-tint">
                Cancelled
                {row.cancelledByName ? ` by ${row.cancelledByName}` : ""}
              </span>
            ) : null}
          </div>

          <Fact icon="clock">
            <time
              dateTime={row.startAt}
              title={absoluteTime(row.startAt)}
              className={cn(!open && "line-through")}
            >
              {longDay(row.startAt)} · {timeRange(row.startAt, row.endAt)}
            </time>
            {minutes ? <span className="text-faint"> ({duration(minutes)})</span> : null}
            {/* The menu has it too, but on a phone -- where a meeting cannot be
                dragged without a long press -- this is the obvious place to
                look, notes or no notes. */}
            {canChange && open && editing !== "reschedule" ? (
              <button
                type="button"
                onClick={() => setEditing("reschedule")}
                className="ml-2 text-xs font-medium text-brand-600 hover:underline dark:text-brand-400"
              >
                Edit
              </button>
            ) : null}
          </Fact>

          {row.mode === "online" ? (
            <Fact icon="video">
              {row.meetingUrl ? (
                <span className="flex flex-wrap items-center gap-2">
                  <a
                    href={row.meetingUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex h-8 items-center rounded-md bg-brand-600 px-3 text-sm font-medium text-white hover:bg-brand-700"
                  >
                    {row.meetingUrl.includes("meet.google.com") ? "Join with Google Meet" : "Join online"}
                  </a>
                  <span className="truncate text-xs text-faint">
                    {row.meetingUrl.replace(/^https?:\/\//, "")}
                  </span>
                </span>
              ) : (
                <span className="text-faint">
                  Online — no link yet.{" "}
                  {row.googleEventId ? "" : "Connect Google Calendar to have one made."}
                </span>
              )}
            </Fact>
          ) : (
            <Fact icon="pin">
              {row.location ? (
                /* A maps link, so a tap puts it in the satnav. */
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(row.location)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-brand-600 hover:underline dark:text-brand-400"
                >
                  {row.location}
                </a>
              ) : (
                <span className="text-faint">No location</span>
              )}
            </Fact>
          )}

          {row.contactName ? (
            <Fact icon="person">
              Seeing {row.contactName}
              {row.contactPhone ? (
                <>
                  {" · "}
                  <a href={`tel:${row.contactPhone}`} className="text-brand-600 hover:underline dark:text-brand-400">
                    {row.contactPhone}
                  </a>
                </>
              ) : null}
            </Fact>
          ) : null}
        </section>

        {/*
          A meeting booked against a name rather than a CRM account -- a new
          prospect, a typo, somebody in a hurry. Linking it is what files its
          notes on the company and lets tasks be raised from it, so it is
          offered right here rather than left for somebody to notice.
        */}
        {!row.companyId ? (
          <LinkCompany
            meeting={row}
            canChange={canChange}
            pending={act.isPending}
            onLink={(company) =>
              act.mutate(() =>
                salesApi.updateMeeting(id, { companyId: company.id, companyName: company.name }),
              )
            }
          />
        ) : null}

        {act.isError ? (
          <p className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700 dark:bg-red-500/10 dark:text-red-300">
            {act.error instanceof ApiError ? act.error.message : "That did not work."}
          </p>
        ) : null}

        {editing === "reschedule" ? (
          <RescheduleForm
            meeting={row}
            pending={act.isPending}
            onCancel={() => setEditing(null)}
            onSave={(patch) => {
              act.mutate(() => salesApi.updateMeeting(id, patch));
              setEditing(null);
            }}
          />
        ) : null}

        {/* ── Who ────────────────────────────────────────────────────────── */}
        <People
          meeting={row}
          user={user}
          editing={editing}
          pending={act.isPending}
          onDone={() => setEditing(null)}
          onChange={(patch) => act.mutate(() => salesApi.updateMeeting(id, patch))}
        />

        {/* ── Notes ─────────────────────────────────────────────────────── */}
        <section>
          <h3 className={SECTION}>Notes</h3>
          <NotesEditor key={row.id} ref={notes} meeting={row} />
        </section>

        {/* ── Follow-up ─────────────────────────────────────────────────── */}
        <section>
          <div className="mb-2 flex items-center gap-2">
            <h3 className={cn(SECTION, "mb-0")}>Follow-up</h3>
            {row.followUps.length === 0 && past && open ? (
              <span className="rounded-full border border-amber-300 px-2 py-0.5 text-[10px] font-medium text-amber-800 dark:border-amber-500/40 dark:text-amber-300">
                Not sent yet
              </span>
            ) : null}
            <Button size="sm" className="ml-auto" onClick={() => setComposing(true)}>
              {row.followUpDraft
                ? "Continue draft"
                : row.followUps.length > 0
                  ? "Write another"
                  : "Write follow-up"}
            </Button>
          </div>
          {row.followUpDraft ? (
            <p className="mb-2 text-xs text-faint">
              Your draft to{" "}
              {row.followUpDraft.to.map((person) => person.name || person.email).join(", ") || "nobody yet"}
              , saved {relativeTime(row.followUpDraft.updatedAt)}.
            </p>
          ) : null}
          {row.followUps.length > 0 ? (
            <ul className="divide-y divide-line rounded-lg border border-line">
              {row.followUps.map((sent) => (
                <SentFollowUp key={sent.id} sent={sent} />
              ))}
            </ul>
          ) : (
            <p className="text-xs text-faint">
              An email from you to {row.guests.length > 0 ? "the people you met" : "them"}, with
              the notes a press away. Sent from your own Gmail and logged on the company.
            </p>
          )}
        </section>

        {/* ── Previous meetings ─────────────────────────────────────────── */}
        {row.previous.length > 0 ? (
          <section>
            <h3 className={SECTION}>Previous meetings with {row.crmCompanyName ?? row.companyName}</h3>
            <ul className="divide-y divide-line rounded-lg border border-line">
              {row.previous.map((previous) => (
                <li key={previous.id}>
                  <button
                    type="button"
                    onClick={() => onOpenMeeting(previous.id)}
                    className="flex w-full flex-col gap-0.5 px-3 py-2 text-left hover:bg-surface-2"
                  >
                    <span className="flex items-baseline gap-2 text-sm">
                      <span className="shrink-0 tabular-nums text-muted">{dayMonth(previous.startAt)}</span>
                      <span className={cn("truncate text-content", previous.status === "cancelled" && "line-through")}>
                        {previous.subject ?? "Meeting"}
                      </span>
                    </span>
                    {previous.notesText ? (
                      <span className="truncate text-xs text-faint">{previous.notesText}</span>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {/* ── History ───────────────────────────────────────────────────── */}
        <details className="group">
          <summary className="cursor-pointer list-none text-[11px] font-medium uppercase tracking-wide text-faint hover:text-content">
            <span className="inline-block transition-transform group-open:rotate-90">▸</span>{" "}
            History{row.details ? " and details" : ""}
          </summary>
          <div className="mt-2 space-y-3">
            {row.details ? (
              <div className="rounded-lg border border-line p-3">
                <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-faint">
                  Before you go
                </p>
                <p className="whitespace-pre-wrap text-sm text-content">{row.details}</p>
              </div>
            ) : null}
            <p className="text-xs text-faint">
              {row.googleEventId
                ? "In Google Calendar — guests are emailed by Google when it changes."
                : "Not in a calendar, so nothing has been sent to anybody."}
              {row.callId ? (
                <>
                  {" "}
                  <Link to={`/sales/calls/${row.callId}`} className="text-brand-600 hover:underline dark:text-brand-400">
                    Booked from a call
                  </Link>
                  .
                </>
              ) : null}
            </p>
            {row.notes.length > 0 ? (
              <ol className="overflow-hidden rounded-lg border border-line">
                {row.notes.map((note) => (
                  <LogEntry key={note.id} note={note} />
                ))}
              </ol>
            ) : null}
          </div>
        </details>
      </div>

      {composing ? (
        <FollowUpComposer meeting={row} user={user} onClose={() => setComposing(false)} />
      ) : null}
    </PanelFrame>
  );
}

/**
 * The pane: a right-hand column on a desktop, the whole screen on a phone.
 *
 * The back arrow on a phone and the cross on a desktop are the same action --
 * close -- drawn the way each platform draws it.
 */
function PanelFrame({
  title,
  onClose,
  actions,
  children,
}: {
  title: string;
  onClose: () => void;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <aside
      aria-label="Meeting"
      className={cn(
        "fixed inset-0 z-40 flex flex-col bg-surface",
        "lg:static lg:z-auto lg:w-[30rem] lg:shrink-0 lg:border-l lg:border-line xl:w-[34rem]",
      )}
    >
      <header className="flex shrink-0 items-center gap-1 border-b border-line px-2 py-2">
        <button
          type="button"
          onClick={onClose}
          aria-label="Back"
          title="Close (Esc)"
          className="flex size-9 items-center justify-center rounded-full text-muted hover:bg-surface-2 hover:text-content"
        >
          {/* An arrow on a phone, a cross beside the calendar. */}
          <svg className="lg:hidden" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          <svg className="hidden lg:block" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
        <h2 className="min-w-0 flex-1 truncate px-1 text-base font-semibold text-content">{title}</h2>
        {actions}
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">{children}</div>
    </aside>
  );
}

/**
 * Link an unlinked meeting to a CRM company, searching as you type -- seeded
 * with the name it was booked under, which is usually the right search.
 */
function LinkCompany({
  meeting,
  canChange,
  pending,
  onLink,
}: {
  meeting: SalesMeetingDetail;
  canChange: boolean;
  pending: boolean;
  onLink: (company: { id: number; name: string }) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(meeting.companyName);

  const matches = useQuery({
    queryKey: queryKeys.crmSearch(query),
    queryFn: () => crmApi.search(query),
    enabled: open && query.trim().length >= 2,
    staleTime: 30_000,
  });

  if (!canChange) {
    return (
      <p className="rounded-lg border border-dashed border-line px-3 py-2 text-xs text-faint">
        Not linked to a CRM company, so its notes stay on the meeting. Somebody going to it can
        link it.
      </p>
    );
  }

  if (!open) {
    return (
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-dashed border-amber-300 px-3 py-2 dark:border-amber-500/40">
        <p className="min-w-0 flex-1 text-xs text-muted">
          Not linked to a CRM company — the notes are not on any company yet, and no tasks can be
          raised.
        </p>
        <Button size="sm" variant="secondary" onClick={() => setOpen(true)}>
          Link to a company
        </Button>
      </div>
    );
  }

  const results = matches.data?.results ?? [];

  return (
    <div className="rounded-lg border border-line p-3">
      <input
        autoFocus
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Search CRM companies"
        aria-label="Search CRM companies"
        className="mb-2 h-8 w-full rounded-md border border-line bg-surface px-2.5 text-sm text-content focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
      />
      {query.trim().length < 2 ? (
        <p className="text-xs text-faint">Type at least two letters.</p>
      ) : matches.isLoading ? (
        <p className="text-xs text-faint">Searching…</p>
      ) : results.length === 0 ? (
        <p className="text-xs text-faint">No CRM company matches that.</p>
      ) : (
        <ul className="max-h-56 divide-y divide-line overflow-y-auto rounded-md border border-line">
          {results.slice(0, 8).map((company) => (
            <li key={company.id}>
              <button
                type="button"
                disabled={pending}
                onClick={() => {
                  onLink({ id: company.id, name: company.name });
                  setOpen(false);
                }}
                className="flex w-full items-baseline gap-2 px-3 py-2 text-left hover:bg-surface-2 disabled:opacity-50"
              >
                <span className="truncate text-sm text-content">{company.name}</span>
                <span className="truncate text-xs text-faint">{company.domain ?? company.city ?? ""}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <Button size="sm" variant="secondary" className="mt-2" onClick={() => setOpen(false)}>
        Cancel
      </Button>
    </div>
  );
}

/**
 * Who was there: ours and theirs, with addresses, and whether each of theirs is
 * somebody the CRM knows on this account. Anybody it does not gets "Add to
 * company", which files them as a contact there and then -- the moment after a
 * visit is when you know who everybody was.
 */
function People({
  meeting,
  user,
  editing,
  pending,
  onDone,
  onChange,
}: {
  meeting: SalesMeetingDetail;
  user: User;
  editing: "who" | "guests" | "reschedule" | null;
  pending: boolean;
  onDone: () => void;
  onChange: (patch: { attendeeUserIds?: number[]; guests?: string[] }) => void;
}) {
  const client = useQueryClient();
  const users = useQuery({
    queryKey: queryKeys.users,
    queryFn: api.users,
    staleTime: 5 * 60_000,
    enabled: editing === "who",
  });

  const addContact = useMutation({
    mutationFn: (guest: { email: string; name: string | null }) =>
      crmApi.createContact(meeting.companyId!, {
        email: guest.email,
        name: guest.name,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.salesMeeting(meeting.id) });
      void client.invalidateQueries({ queryKey: ["crm", "contacts", meeting.companyId] });
    },
  });

  return (
    <section>
      <h3 className={SECTION}>
        {meeting.attendees.length + meeting.guests.length} going
      </h3>
      <ul className="space-y-1.5">
        {meeting.attendees.map((person) => (
          <li key={`u${person.userId}`} className="flex items-center gap-2.5">
            <Avatar agent={{ id: person.userId, name: person.name }} size="sm" />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm text-content">
                {person.userId === user.id ? `${person.name ?? "You"} (you)` : (person.name ?? "Somebody")}
              </span>
              <span className="block truncate text-xs text-faint">{person.email} · NetXL</span>
            </span>
          </li>
        ))}
        {meeting.guests.map((guest) => (
          <li key={guest.email} className="flex items-center gap-2.5">
            <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-surface-2 text-xs font-medium text-muted-on-tint">
              {(guest.name ?? guest.email)[0]!.toUpperCase()}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm text-content">
                {guest.name ?? guest.email}
              </span>
              <span className="block truncate text-xs text-faint">
                {guest.email}
                {guest.onCompany ? " · contact" : guest.contactId ? " · contact elsewhere" : ""}
              </span>
            </span>
            {!guest.onCompany && meeting.companyId ? (
              <button
                type="button"
                disabled={addContact.isPending}
                onClick={() => addContact.mutate(guest)}
                className="shrink-0 rounded-md border border-line px-2 py-1 text-[11px] text-muted hover:bg-surface-2 hover:text-brand-600 disabled:opacity-50 dark:hover:text-brand-400"
              >
                Add to company
              </button>
            ) : null}
          </li>
        ))}
      </ul>
      {addContact.isError ? (
        <p className="mt-1 text-xs text-red-600">
          {addContact.error instanceof ApiError ? addContact.error.message : "Could not add them."}
        </p>
      ) : null}
      {meeting.guests.length > 0 && !meeting.googleEventId ? (
        <p className="mt-1.5 text-[11px] text-amber-700 dark:text-amber-400">
          Not in a calendar, so the guests have not been sent an invitation.
        </p>
      ) : null}

      {editing === "who" ? (
        <div className="mt-3 rounded-lg border border-line p-3">
          <p className="mb-2 text-xs text-muted">Who from NetXL is going</p>
          <div className="flex flex-wrap gap-1.5">
            {(users.data ?? []).map((person) => {
              const on = meeting.attendees.some((a) => a.userId === person.id);
              return (
                <Chip
                  key={person.id}
                  on={on}
                  onClick={() =>
                    onChange({
                      attendeeUserIds: on
                        ? meeting.attendees.filter((a) => a.userId !== person.id).map((a) => a.userId)
                        : [...meeting.attendees.map((a) => a.userId), person.id],
                    })
                  }
                >
                  {person.id === user.id ? `${person.name} (you)` : person.name}
                </Chip>
              );
            })}
          </div>
          <Button size="sm" variant="secondary" className="mt-2" onClick={onDone}>
            Done
          </Button>
        </div>
      ) : null}

      {editing === "guests" ? (
        <GuestEditor
          meeting={meeting}
          pending={pending}
          onCancel={onDone}
          onSave={(emails) => {
            onChange({ guests: emails });
            onDone();
          }}
        />
      ) : null}
    </section>
  );
}

/**
 * Change who is invited, with one explicit save at the end -- each save is
 * Google emailing people, so adding three guests should be one round of
 * invitations sent when you say so, not three as you think out loud.
 */
function GuestEditor({
  meeting,
  pending,
  onCancel,
  onSave,
}: {
  meeting: SalesMeetingDetail;
  pending: boolean;
  onCancel: () => void;
  onSave: (emails: string[]) => void;
}) {
  const [draft, setDraft] = useState(() => meeting.guests.map((guest) => guest.email));
  const was = meeting.guests.map((guest) => guest.email);
  const added = draft.filter((email) => !was.includes(email));
  const removed = was.filter((email) => !draft.includes(email));
  const changed = added.length > 0 || removed.length > 0;

  return (
    <div className="mt-3 space-y-2 rounded-lg border border-line p-3">
      <GuestPicker companyId={meeting.companyId} value={draft} onChange={setDraft} disabled={pending} />
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" disabled={!changed || pending} onClick={() => onSave(draft)}>
          Save guests
        </Button>
        <Button size="sm" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <p className="text-[11px] text-faint">
          {!changed
            ? "No changes yet."
            : [
                added.length > 0 ? `${added.length} to be invited by email` : null,
                removed.length > 0 ? `${removed.length} to be uninvited` : null,
                !meeting.googleEventId && added.length > 0 ? "this puts it in your Google calendar" : null,
              ]
                .filter(Boolean)
                .join("; ")}
        </p>
      </div>
    </div>
  );
}

/**
 * Rename it, move it, and move where it is. Inline rather than another dialog
 * over the pane: on a phone the pane *is* the screen, and a dialog over it is
 * a third layer to close.
 *
 * The title is here because a meeting booked by dragging on the calendar, or
 * pulled in from Google, often arrives without a useful one, and the notes
 * written afterwards are filed on the company under it. Open to the same
 * people as the rest, and still open once there are notes -- naming a visit
 * after the fact is exactly when somebody knows what it was about.
 */
function RescheduleForm({
  meeting,
  pending,
  onCancel,
  onSave,
}: {
  meeting: SalesMeetingDetail;
  pending: boolean;
  onCancel: () => void;
  onSave: (patch: {
    subject?: string | null;
    startAt?: string;
    endAt?: string | null;
    location?: string | null;
    meetingUrl?: string | null;
    mode?: "in_person" | "online";
  }) => void;
}) {
  const [subject, setSubject] = useState(meeting.subject ?? "");
  const [startAt, setStartAt] = useState(asInput(meeting.startAt));
  const [mode, setMode] = useState(meeting.mode);
  const [location, setLocation] = useState(meeting.location ?? "");
  const [meetingUrl, setMeetingUrl] = useState(meeting.meetingUrl ?? "");

  /* However long it was, kept as it moves. */
  const minutes = meeting.endAt
    ? (new Date(meeting.endAt).getTime() - new Date(meeting.startAt).getTime()) / 60_000
    : null;

  return (
    <div className="space-y-3 rounded-lg border border-line p-3">
      <label className="block">
        <span className={SECTION}>Title</span>
        <input
          value={subject}
          onChange={(event) => setSubject(event.target.value)}
          maxLength={255}
          placeholder={`Meeting with ${meeting.companyName}`}
          className="w-full rounded-md border border-line bg-surface px-2.5 py-1.5 text-sm text-content placeholder:text-faint"
        />
      </label>
      <label className="block">
        <span className={SECTION}>When</span>
        <input
          type="datetime-local"
          value={startAt}
          onChange={(event) => setStartAt(event.target.value)}
          className="w-full rounded-md border border-line bg-surface px-2.5 py-1.5 text-sm text-content"
        />
      </label>
      <div>
        <span className={SECTION}>Where</span>
        <div className="mb-2 flex gap-1.5">
          <Chip on={mode === "in_person"} onClick={() => setMode("in_person")}>
            In person
          </Chip>
          <Chip on={mode === "online"} onClick={() => setMode("online")}>
            Online
          </Chip>
        </div>
        {mode === "in_person" ? (
          <input
            value={location}
            onChange={(event) => setLocation(event.target.value)}
            placeholder="Their office, or a postcode"
            className="w-full rounded-md border border-line bg-surface px-2.5 py-1.5 text-sm text-content placeholder:text-faint"
          />
        ) : (
          <>
            <input
              type="url"
              value={meetingUrl}
              onChange={(event) => setMeetingUrl(event.target.value)}
              placeholder="Leave empty for a Google Meet link, or paste one"
              className="w-full rounded-md border border-line bg-surface px-2.5 py-1.5 text-sm text-content placeholder:text-faint"
            />
            {!meetingUrl.trim() ? (
              <p className="mt-1 text-[11px] text-faint">A Google Meet link is made when you save.</p>
            ) : null}
          </>
        )}
      </div>
      <div className="flex gap-2">
        <Button
          size="sm"
          disabled={pending || !startAt}
          onClick={() =>
            onSave({
              subject: subject.trim() || null,
              startAt: new Date(startAt).toISOString(),
              endAt:
                minutes === null
                  ? null
                  : new Date(new Date(startAt).getTime() + minutes * 60_000).toISOString(),
              mode,
              location: location.trim() || null,
              meetingUrl: meetingUrl.trim() || null,
            })
          }
        >
          Save
        </Button>
        <Button size="sm" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function SentFollowUp({ sent }: { sent: SalesMeetingDetail["followUps"][number] }) {
  return (
    <li>
      <details className="group px-3 py-2">
        <summary className="flex cursor-pointer list-none items-baseline gap-2 text-sm">
          <span className="min-w-0 flex-1 truncate text-content">
            To {sent.to.map((person) => person.name || person.email).join(", ")}
          </span>
          <span className="shrink-0 text-xs text-faint" title={absoluteTime(sent.sentAt)}>
            {sent.userName ? `${sent.userName.split(" ")[0]}, ` : ""}
            {relativeTime(sent.sentAt)}
          </span>
        </summary>
        <p className="mt-1 text-xs font-medium text-muted">{sent.subject}</p>
        {/* Sanitised on the server when it was sent; this is our own copy. */}
        <div
          className="prose-sm mt-1 max-w-none text-sm text-content [&_ul]:list-disc [&_ul]:pl-5"
          dangerouslySetInnerHTML={{ __html: sent.bodyHtml }}
        />
      </details>
    </li>
  );
}

/** One line of the meeting's log, quieter for the server's own lines. */
function LogEntry({ note }: { note: SalesMeetingNote }) {
  if (note.kind === "system") {
    return (
      <li className="flex flex-wrap items-baseline gap-x-2 border-b border-line px-3 py-1.5 text-xs text-faint last:border-b-0">
        <span>{note.body}</span>
        <span>
          {note.userName ? `${note.userName}, ` : ""}
          {relativeTime(note.createdAt)}
        </span>
      </li>
    );
  }

  return (
    <li className="flex gap-3 border-b border-line px-3 py-2 last:border-b-0">
      <Avatar agent={{ id: note.userId ?? 0, name: note.userName }} size="xs" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 text-xs text-faint">
          {note.kind === "task" ? (
            <span className="rounded-full bg-surface-2 px-1.5 text-[10px] font-medium text-muted-on-tint">Task</span>
          ) : null}
          <span>
            {note.userName ?? "Somebody"}, {relativeTime(note.createdAt)}
          </span>
          {note.taskAssignee ? <span>for {note.taskAssignee}</span> : null}
          {note.taskDueAt ? <span>due {dayMonth(note.taskDueAt)}</span> : null}
        </div>
        <p className="mt-0.5 whitespace-pre-wrap text-sm text-content">{note.body}</p>
        {note.taskUrl ? (
          <a href={note.taskUrl} target="_blank" rel="noreferrer" className="text-[11px] text-brand-600 hover:underline">
            Open in Lane
          </a>
        ) : null}
      </div>
    </li>
  );
}

export function Fact({
  icon,
  children,
}: {
  icon: "clock" | "pin" | "video" | "person" | "building" | "notes";
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3 text-sm text-content">
      <svg className="mt-0.5 size-4 shrink-0 text-faint" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        {icon === "clock" ? (
          <>
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3 2" />
          </>
        ) : icon === "pin" ? (
          <>
            <path d="M12 21s-7-6.2-7-11a7 7 0 0 1 14 0c0 4.8-7 11-7 11z" />
            <circle cx="12" cy="10" r="2.5" />
          </>
        ) : icon === "video" ? (
          <>
            <rect x="3" y="6" width="13" height="12" rx="2" />
            <path d="m16 10 5-3v10l-5-3" />
          </>
        ) : icon === "building" ? (
          <>
            <path d="M4 21V5l8-2v18M12 9h8v12M8 8h.01M8 12h.01M8 16h.01M16 13h.01M16 17h.01M2 21h20" />
          </>
        ) : icon === "notes" ? (
          <>
            <path d="M5 4h14v16H5z" />
            <path d="M9 9h6M9 13h6M9 17h3" />
          </>
        ) : (
          <>
            <circle cx="12" cy="8" r="4" />
            <path d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6" />
          </>
        )}
      </svg>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

function Menu({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onDown = (event: PointerEvent): void => {
      if (!ref.current?.contains(event.target as Node)) onClose();
    };
    window.addEventListener("pointerdown", onDown, true);
    return () => window.removeEventListener("pointerdown", onDown, true);
  }, [onClose]);
  return (
    <div
      ref={ref}
      role="menu"
      onClick={onClose}
      className="absolute right-0 top-9 z-50 w-56 overflow-hidden rounded-lg border border-line bg-surface py-1 shadow-xl"
    >
      {children}
    </div>
  );
}

function MenuItem({
  onClick,
  danger,
  children,
}: {
  onClick: () => void;
  danger?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={cn(
        "block w-full px-3 py-2 text-left text-sm hover:bg-surface-2",
        danger ? "text-red-600 dark:text-red-400" : "text-content",
      )}
    >
      {children}
    </button>
  );
}

const SECTION = "mb-2 block text-[11px] font-medium uppercase tracking-wide text-faint";

export function duration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours} hr ${rest} min` : `${hours} hr`;
}

/** An ISO string as a `datetime-local` value, from local parts. */
function asInput(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
